var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useCallback } from 'react';
import { useQuery } from '@apollo/client/react';
import { Trans } from '@lingui/react/macro';
import { Table, TableHead, TableBody, TableHeadCell, TableHeaderText, TableRow } from '@pocketlaw/tetris';
import { useDocumentPreviewList } from 'app/domains/documents';
import { EntryKind } from 'shared/domains/apollo/generated/types';
import { FileSystemTableContractOwnersDocument } from './contractOwnersQuery.gql';
import FileSystemTableRow from '../FileSystemTableRow';
import { Container } from './styled';
const getContractOwnerIds = (entry) => { var _a, _b; return 'contractOwner' in entry ? ((_b = (_a = entry.contractOwner[0]) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : []) : []; };
function FileSystemTable(props) {
    var _a;
    const { sortId, sortOrder, onDuplicate, onRename, onMove, onQuickMove, onDelete, entries, onSort } = props;
    const userIds = [...new Set(entries.flatMap(getContractOwnerIds))];
    const { data } = useQuery(FileSystemTableContractOwnersDocument, {
        variables: { userIds },
        skip: !userIds.length,
    });
    const usersById = new Map((_a = data === null || data === void 0 ? void 0 : data.users.map((user) => [user.id, user])) !== null && _a !== void 0 ? _a : []);
    useDocumentPreviewList(entries.filter((entry) => entry.kind === EntryKind.Document).map((entry) => entry.id));
    const handleQuickMove = useCallback((quickMoveData) => {
        var _a, _b;
        const { type, sourceId, targetId } = quickMoveData;
        const sourceName = (_a = entries.find(({ id }) => id === sourceId)) === null || _a === void 0 ? void 0 : _a.name;
        const targetName = (_b = entries.find(({ id }) => id === targetId)) === null || _b === void 0 ? void 0 : _b.name;
        onQuickMove({ type, sourceId, targetId, sourceName, targetName });
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entries]);
    return (<Container>
      <Table border sortId={sortId} sortOrder={sortOrder} onSort={(id, order) => {
            if (id) {
                onSort(id, order);
            }
        }}>
        <colgroup>
          <col width="auto"/>
          <col width={256}/>
          <col width={128}/>
          <col width="auto"/>
          <col width={48}/>
        </colgroup>
        <TableHead>
          <TableRow>
            <TableHeadCell id="filesystemtable-name">
              <TableHeaderText sortId="name">
                <Trans comment="Files: table column head for the entry title">Title</Trans>
              </TableHeaderText>
            </TableHeadCell>
            <TableHeadCell id="filesystemtable-status">
              <TableHeaderText>
                <Trans comment="Files: table column head for the entry status">Status</Trans>
              </TableHeaderText>
            </TableHeadCell>
            <TableHeadCell id="filesystemtable-updatedAt">
              <TableHeaderText sortId="updatedAt">
                <Trans comment="Files: table column head for the entry updated date">Updated</Trans>
              </TableHeaderText>
            </TableHeadCell>
            <TableHeadCell id="filesystemtable-contractOwner">
              <TableHeaderText>
                <Trans comment="Files: table column head for the contract owner">Contract owner</Trans>
              </TableHeaderText>
            </TableHeadCell>
            <TableHeadCell id="filesystemtable-contextmenu"/>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map((entry) => {
            const { id, kind } = entry, rest = __rest(entry, ["id", "kind"]);
            const owners = getContractOwnerIds(entry).flatMap((ownerId) => {
                var _a;
                const user = usersById.get(ownerId);
                return user ? [{ id: user.id, name: (_a = user.name) !== null && _a !== void 0 ? _a : '' }] : [];
            });
            return (<FileSystemTableRow {...rest} key={id} id={id} type={kind} owners={owners} onDuplicate={onDuplicate} onRename={onRename} onMove={onMove} onDelete={onDelete} onQuickMove={handleQuickMove}/>);
        })}
        </TableBody>
      </Table>
    </Container>);
}
export default FileSystemTable;
